import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import EastIcon from "@mui/icons-material/East";
import { put, api, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../GRI308/SectionHeading";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";
import CustomTextField from "../GRI308/CustomTextField";

const GRI304_4 = ({hide}) => {
    const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));
const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    totalSpecies: "",
    extinctionLevels: [
      { id: 1, category: "Critically Endangered", number: "" },
      { id: 2, category: "Endangered", number: "" },
      { id: 3, category: "Vulnerable", number: "" },
      { id: 4, category: "Near Threatened", number: "" },
      { id: 5, category: "Least Concern", number: "" },
    ],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?Id=${id}&type=GRI300`);
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);
        const data =
          response.data.data.GRI300?.iucnRedListConservationSpecies || {};
        // Populating formData with fetched data
        setFormData((prevFormData) => ({
          ...prevFormData,
          totalSpecies: data.conservationSpeciesCount || "", // Set totalSpecies
          extinctionLevels: prevFormData.extinctionLevels.map((level) => {
            const levelData = Array.isArray(data.levelofExtinction)
              ? data.levelofExtinction.find(
                  (extinctionLevel) =>
                    extinctionLevel.destination === level.category
                )
              : null;
            return {
              ...level,
              number: levelData ? levelData.numbers : "",
            };
          }),
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e, id) => {
    const value = e.target.value;
    const isValidInput = /^\d*$/.test(value);

    if (isValidInput) {
      // Remove validation error when input is valid
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[
          id === "totalSpecies" ? "totalSpecies" : `extinctionLevel_${id}`
        ];
        return newErrors;
      });

      if (id === "totalSpecies") {
        setFormData({ ...formData, totalSpecies: value });
      } else {
        const updatedExtinctionLevels = formData.extinctionLevels.map((level) =>
          level.id === id ? { ...level, number: value } : level
        );
        setFormData({ ...formData, extinctionLevels: updatedExtinctionLevels });
      }
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // if (!formData.totalSpecies) {
    //     newErrors.totalSpecies = "This field is required";
    //     formIsValid = false;
    //   } else
    if (isNaN(formData.totalSpecies) || formData.totalSpecies < 0) {
      newErrors.totalSpecies = "Please enter a valid numeric value";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return;

      // Prepare the payload based on the schema
      const payload = {
        type: "GRI300",
        submitted304_4: true,
        iucnRedListConservationSpecies: {
          conservationSpeciesCount: parseInt(formData.totalSpecies, 10),
          levelofExtinction: formData.extinctionLevels.map((level) => ({
            destination: level.category,
            numbers: parseInt(level.number, 10),
          })),
        },
      };
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri300/305-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri300/305-1/${id}`);
    }
  };


  const renderContent = ({hide}) => (
     <>
      {!hide && <p className={styles.section_heading}>304 : Biodiversity</p>}
      <p className={styles.main_heading} style={{marginBottom : hide ? "-1rem" : "0", }}>
        304-4 : IUCN Red List species and national conservation list species
        with habitats in areas affected by operations
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <SectionHeading text="a. How many IUCN Red List species and national conservation list species with habitats in areas affected by the operations of the organization were identified?" />

        {/* Total Number Input */}
        <Box sx={{ marginLeft: "1rem", marginTop: "-0.7rem" }}>
          <SectionHeading
            text="Total number of Species Affected"
            sx={{ fontWeight: 500, fontSize: "15px" }}
          />
          <Box sx={{ width: "30%", marginLeft: "-1rem" }}>
            <CustomTextField
              value={formData.totalSpecies}
              onChange={(e) => handleChange(e, "totalSpecies")}
              placeholder={"Enter Number"}
              error={errors.totalSpecies}
              helperText={errors.totalSpecies}
              disabled={disabled}
            />
          </Box>
        </Box>

        {/* Table */}
        <SectionHeading text="Level of Extinction Risk" />

        <SectionHeading
          text="For each affected species, categorize the level of extinction risk based
        on the following classifications:"
          sx={{ marginTop: "-0.7rem" }}
        />

        <Typography></Typography>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Table
            sx={{
              "& .MuiTableHead-root": {
                backgroundColor: "#1C486B", // Header background color
              },
              "& .MuiTableCell-root": {
                borderRight: "1px solid #E0E0E0",
              },
              "& .MuiTableCell-root:last-child": {
                borderRight: "none", // Remove last column border
              },
              "& .MuiTableRow-root": {
                height: "1rem",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ width: "100%" }}>
                <TableCell sx={{ color: "#FFFFFF", width: "8%" }}>
                  S. No.
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", width: "46%" }}>
                  Destination
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", width: "46%" }}>
                  Numbers
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.extinctionLevels.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell sx={{ color: "#1C486B" }}>
                    {row.category}
                  </TableCell>
                  <TableCell>
                    <TextField
                      placeholder="Enter Number"
                      value={row.number}
                      onChange={(e) => handleChange(e, row.id)}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiInputBase-input": {
                          padding: "0px 5px",
                        },
                      }}
                      disabled={disabled}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Submit Button */}
       

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  )

  return (
     <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
             {!griReview ? (
          <Box sx={{ ml: "1rem", width: "98%" }}>
            <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
          </Box>
        ) : (
          <Box textAlign="right" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              sx={{ bgcolor: "#1C486B", textTransform: "none", mt: 3, mb: 5 }}
              onClick={handleSubmit}
            >
              Save & Continue <EastIcon sx={{ ml: 2 }} />
            </Button>
          </Box>
        )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI304_4;
